import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Hash } from 'lucide-react';

/**
 * ReferenceCombobox — searchable single-select for picking an EXISTING
 * Page/Reference number to link an order to.
 *
 * A number may be SHARED only across orders that match on all three of
 * Customer + Goldsmith + Gold Purity, so this fetches the scoped options from
 * ``GET /orders/reference-options`` (company + customer + goldsmith + purity)
 * and dedupes by number. Emits the chosen number (int) via ``onChange`` — or
 * ``null`` when cleared. Renders no options until all three scope values are
 * supplied.
 *
 * Props:
 *   companyId   string   active company id
 *   customerId  string   customer whose numbers to list (required)
 *   goldsmithId string   goldsmith scope (required)
 *   purityId    string   gold-purity scope (required)
 *   value       number|null  currently-picked number
 *   onChange    (n: number|null) => void
 *   label       string   display term (e.g. "Page No.")
 *   disabled    boolean
 *   testId      string
 */
export function ReferenceCombobox({
  companyId,
  customerId,
  goldsmithId,
  purityId,
  value,
  onChange,
  label = 'Page No.',
  disabled = false,
  testId = 'reference-combobox',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const labelShort = (label.split(' ')[0] || label).replace(/\.$/, '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // All three scope values are required to share a number.
      if (!companyId || !customerId || !goldsmithId || !purityId) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get('/orders/reference-options', {
          params: {
            company_id: companyId,
            customer_id: customerId,
            goldsmith_id: goldsmithId,
            purity_id: purityId,
          },
        });
        // Dedupe by reference_no; keep a representative order + collect titles.
        const map = new Map();
        for (const o of (data || [])) {
          const rn = o.reference_no;
          if (rn == null) continue;
          if (!map.has(rn)) {
            map.set(rn, {
              reference_no: rn,
              order_no: o.order_no,
              titles: o.title ? [o.title] : [],
              count: 1,
            });
          } else {
            const e = map.get(rn);
            e.count += 1;
            if (o.title) e.titles.push(o.title);
          }
        }
        const list = Array.from(map.values()).sort(
          (a, b) => Number(b.reference_no) - Number(a.reference_no),
        );
        if (!cancelled) setItems(list);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, customerId, goldsmithId, purityId]);

  return (
    <SearchableSelect
      items={items}
      value={value ?? ''}
      onValueChange={(v) => onChange(v ? Number(v) : null)}
      itemToValue={(it) => it.reference_no}
      itemToLabel={(it) => `${it.reference_no}${it.order_no ? ` · ${it.order_no}` : ''}`}
      itemToSubLabel={(it) => {
        const t = (it.titles || []).filter(Boolean);
        const titleStr = t.length ? t.join(', ') : '—';
        return it.count > 1 ? `${it.count} orders · ${titleStr}` : titleStr;
      }}
      searchKeys={(it) => `${it.reference_no} ${it.order_no || ''} ${(it.titles || []).join(' ')}`}
      placeholder={loading ? 'Loading…' : `Select existing ${labelShort}…`}
      searchPlaceholder={`Search ${labelShort.toLowerCase()} / order / title…`}
      leadingIcon={<Hash size={14} />}
      loading={loading}
      disabled={disabled}
      emptyText={`No existing ${labelShort.toLowerCase()}s for this customer, goldsmith & purity.`}
      testId={testId}
      allowClear
    />
  );
}

export default ReferenceCombobox;
